import React, { useMemo } from 'react';
import { Geofence, GeofenceShapeType } from '@models/geofence.model';
import {
  GeofenceCategoryIcon,
  DEFAULT_GEOFENCE_COLOR,
} from '@utils/geofenceCategoryIcons';
import { polygonCentroid } from '@utils/googleMapsLink';
import GoogleMapsLinkButton from '@components/atoms/GoogleMapsLinkButton';
import RoomIcon from '@mui/icons-material/Room';
import PolylineIcon from '@mui/icons-material/Polyline';
import StraightenIcon from '@mui/icons-material/Straighten';
import LinkIcon from '@mui/icons-material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface GeofenceInfoCardProps {
  geofence: Geofence | null;
}

const formatRadius = (meters: number) => {
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${Math.round(meters)} m`;
};

export const GeofenceInfoCard: React.FC<GeofenceInfoCardProps> = ({ geofence }) => {
  // Pick a reasonable point for navigation — circle center, or polygon
  // centroid if there's no center. Falls through to nulls when neither is
  // available so the button hides itself. Computed before any early return so
  // hook order stays stable.
  const navCoords = useMemo(() => {
    if (!geofence) return null;
    if (geofence.shape.center) return geofence.shape.center;
    if (
      geofence.shape.type === GeofenceShapeType.POLYGON &&
      geofence.shape.polygonCoordinates &&
      geofence.shape.polygonCoordinates.length > 0
    ) {
      return polygonCentroid(geofence.shape.polygonCoordinates);
    }
    return null;
  }, [geofence]);

  if (!geofence) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-white p-4 flex items-center gap-3 text-sm text-gray-500">
        <InfoOutlinedIcon className="text-gray-300" sx={{ fontSize: 20 }} />
        Select a geofence from the table to see its details here.
      </div>
    );
  }

  const color =
    geofence.category?.color || geofence.color || DEFAULT_GEOFENCE_COLOR;
  const isCircle = geofence.shape.type === GeofenceShapeType.POINT_RADIUS;
  const ShapeIcon = isCircle ? RoomIcon : PolylineIcon;
  const shapeLabel = isCircle ? 'Circle' : 'Polygon';

  const linkedLabel = geofence.project?.name
    ? `Project · ${geofence.project.name}`
    : geofence.warehouse?.name
      ? `Warehouse · ${geofence.warehouse.name}`
      : 'Standalone';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}20`, color }}
          >
            {geofence.category ? (
              <GeofenceCategoryIcon
                iconKey={geofence.category.icon}
                sx={{ fontSize: 22 }}
              />
            ) : (
              <ShapeIcon sx={{ fontSize: 22 }} />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {geofence.name}
              </p>
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full ${
                  geofence.isActive
                    ? 'bg-green-50 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {geofence.isActive ? 'Active' : 'Disabled'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {geofence.category?.name || 'Uncategorized'}
            </p>
          </div>
        </div>
      </div>

      {geofence.description && (
        <p className="text-xs text-gray-600 mt-3 leading-relaxed">
          {geofence.description}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 text-[11px] font-medium text-gray-700">
          <ShapeIcon sx={{ fontSize: 14 }} />
          {shapeLabel}
        </span>
        {isCircle && geofence.shape.radiusMeters && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 text-[11px] font-medium text-gray-700">
            <StraightenIcon sx={{ fontSize: 14 }} />
            {formatRadius(geofence.shape.radiusMeters)}
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 text-[11px] font-medium text-gray-700">
          <LinkIcon sx={{ fontSize: 14 }} />
          {linkedLabel}
        </span>
        {navCoords && (
          <GoogleMapsLinkButton
            lat={navCoords.lat}
            lng={navCoords.lng}
            label="Directions"
          />
        )}
      </div>
    </div>
  );
};

export default GeofenceInfoCard;
